import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NamedEntityOption } from '../types';

interface LabeledOption {
  value: string;
  label: string;
}

interface JobListingAttributes {
  title?: string | null;
  internal_job_listing_id?: string | null;
  company_name?: string | null;
  url?: string | null;
  description?: string | null;
  min_reward?: number | string | null;
  max_reward?: number | string | null;
  reward_unit?: string | null;
  location_name?: string | null;
  contract_type_name?: string | null;
  workstyle?: string | null;
  workstyle_type?: string | null;
  availability?: string | null;
  availability_type?: string | null;
  required_skill?: string | null;
  preferred_skill?: string | null;
  status?: string | null;
  recommendation_points?: string | null;
}

interface JobListingFormState {
  title: string;
  internal_job_listing_id: string;
  company_name: string;
  url: string;
  description: string;
  min_reward: string;
  max_reward: string;
  reward_unit: string;
  location_name: string;
  contract_type_name: string;
  workstyle: string;
  workstyle_type: string;
  availability: string;
  availability_type: string;
  required_skill: string;
  preferred_skill: string;
  status: string;
  recommendation_points: string[];
}

interface JobListingFormProps {
  postUrl: string;
  backUrl: string;
  authenticityToken: string;
  jobListing: JobListingAttributes;
  generateInternalIdUrl: string;
  workstyleTypeOptions: LabeledOption[];
  availabilityTypeOptions: LabeledOption[];
  statusOptions: LabeledOption[];
  selectedSkills: NamedEntityOption[];
  selectedOccupations: NamedEntityOption[];
  selectedIndustries: NamedEntityOption[];
  errors: string[];
  heading?: string;
  description?: string;
  submitLabel?: string;
  submittingLabel?: string;
  method?: 'post' | 'patch' | 'put';
}

const parseRecommendationPoints = (recommendationPoints: string | null | undefined): string[] => {
  if (!recommendationPoints) return [];
  
  try {
    const parsed = JSON.parse(recommendationPoints);
    return parsed.points || [];
  } catch {
    return [];
  }
};

const emptyStateFrom = (jobListing: JobListingAttributes): JobListingFormState => ({
  title: jobListing.title ?? '',
  internal_job_listing_id: jobListing.internal_job_listing_id ?? '',
  company_name: jobListing.company_name ?? '',
  url: jobListing.url ?? '',
  description: jobListing.description ?? '',
  min_reward: jobListing.min_reward?.toString() ?? '',
  max_reward: jobListing.max_reward?.toString() ?? '',
  reward_unit: jobListing.reward_unit ?? '',
  location_name: jobListing.location_name ?? '',
  contract_type_name: jobListing.contract_type_name ?? '',
  workstyle: jobListing.workstyle ?? '',
  workstyle_type: jobListing.workstyle_type ?? '',
  availability: jobListing.availability ?? '',
  availability_type: jobListing.availability_type ?? '',
  required_skill: jobListing.required_skill ?? '',
  preferred_skill: jobListing.preferred_skill ?? '',
  status: jobListing.status ?? 'public',
  recommendation_points: parseRecommendationPoints(jobListing.recommendation_points)
});

const applySelectedFilter = (
  options: NamedEntityOption[],
  selected: NamedEntityOption[]
) => {
  const selectedIds = new Set(selected.map((item) => item.id));
  return options.filter((option) => !selectedIds.has(option.id));
};

const sortByCount = (options: NamedEntityOption[], key: 'job_listing_total_count' | 'job_listing_count') => {
  return [...options].sort((a, b) => {
    const aCount = (a[key] ?? 0) as number;
    const bCount = (b[key] ?? 0) as number;
    return bCount - aCount;
  });
};

const JobListingForm: React.FC<JobListingFormProps> = ({
  postUrl,
  backUrl,
  authenticityToken,
  jobListing,
  generateInternalIdUrl,
  workstyleTypeOptions,
  availabilityTypeOptions,
  statusOptions,
  selectedSkills: initialSkills,
  selectedOccupations: initialOccupations,
  selectedIndustries: initialIndustries,
  errors,
  heading,
  description,
  submitLabel,
  submittingLabel,
  method
}) => {
  const [form, setForm] = useState<JobListingFormState>(emptyStateFrom(jobListing));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingInternalId, setIsGeneratingInternalId] = useState(false);
  const [internalIdError, setInternalIdError] = useState<string | null>(null);
  const generatingInternalIdRef = useRef(false);

  const formHeading = heading ?? '求人を追加';
  const formDescription = description ?? '案件番号とスキル・職種・業界を先に決めることで、求人を簡単に作成できます。';
  const submitText = submitLabel ?? '求人を作成する';
  const submittingText = submittingLabel ?? '作成中…';
  const httpMethod = method ?? 'post';
  const methodOverride = httpMethod === 'post' ? null : httpMethod;

  const [selectedSkills, setSelectedSkills] = useState<NamedEntityOption[]>(initialSkills || []);
  const [selectedOccupations, setSelectedOccupations] = useState<NamedEntityOption[]>(initialOccupations || []);
  const [selectedIndustries, setSelectedIndustries] = useState<NamedEntityOption[]>(initialIndustries || []);

  const [skills, setSkills] = useState<NamedEntityOption[]>([]);
  const [skillSuggestions, setSkillSuggestions] = useState<NamedEntityOption[]>([]);
  const [skillQuery, setSkillQuery] = useState('');

  const [occupations, setOccupations] = useState<NamedEntityOption[]>([]);
  const [occupationQuery, setOccupationQuery] = useState('');

  const [industries, setIndustries] = useState<NamedEntityOption[]>([]);
  const [industrySuggestions, setIndustrySuggestions] = useState<NamedEntityOption[]>([]);
  const [industryQuery, setIndustryQuery] = useState('');

  const generateInternalJobListingId = useCallback(async () => {
    if (generatingInternalIdRef.current) {
      return;
    }

    generatingInternalIdRef.current = true;
    setIsGeneratingInternalId(true);
    setInternalIdError(null);

    try {
      const response = await fetch(generateInternalIdUrl, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to generate internal job listing id (${response.status})`);
      }

      const data: { internal_job_listing_id?: string } = await response.json();

      if (data.internal_job_listing_id) {
        setForm((prev) => ({
          ...prev,
          internal_job_listing_id: data.internal_job_listing_id
        }));
      } else {
        throw new Error('Response did not include internal_job_listing_id');
      }
    } catch (error) {
      console.error(error);
      setInternalIdError('案件番号の自動生成に失敗しました。時間をおいて再度お試しください。');
    } finally {
      generatingInternalIdRef.current = false;
      setIsGeneratingInternalId(false);
    }
  }, [generateInternalIdUrl]);

  useEffect(() => {
    if (!jobListing.internal_job_listing_id) {
      void generateInternalJobListingId();
    }

    const loadSkills = async () => {
      try {
        const response = await fetch('/api/v1/skills', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Failed to load skills (${response.status})`);
        const groupedData = await response.json();
        
        // グループ化されたデータを平坦化
        const flatSkills: NamedEntityOption[] = [];
        groupedData.forEach((group: any) => {
          if (group.skills && Array.isArray(group.skills)) {
            flatSkills.push(...group.skills);
          }
        });
        
        setSkills(flatSkills);
        const suggestions = sortByCount(flatSkills, 'job_listing_total_count');
        setSkillSuggestions(applySelectedFilter(suggestions, initialSkills || []).slice(0, 10));
      } catch (error) {
        console.error(error);
      }
    };

    const loadOccupations = async () => {
      try {
        const response = await fetch('/api/v1/occupations', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Failed to load occupations (${response.status})`);
        const data: NamedEntityOption[] = await response.json();
        setOccupations(data);
      } catch (error) {
        console.error(error);
      }
    };

    const loadIndustries = async () => {
      try {
        const response = await fetch('/api/v1/industries', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Failed to load industries (${response.status})`);
        const data: NamedEntityOption[] = await response.json();
        setIndustries(data);
        const suggestions = sortByCount(data, 'job_listing_total_count');
        setIndustrySuggestions(applySelectedFilter(suggestions, initialIndustries || []).slice(0, 10));
      } catch (error) {
        console.error(error);
      }
    };

    loadSkills();
    loadOccupations();
    loadIndustries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const controller = new AbortController();
    const term = skillQuery.trim();

    const run = async () => {
      if (term.length === 0) {
        const suggestions = sortByCount(skills, 'job_listing_total_count');
        setSkillSuggestions(applySelectedFilter(suggestions, selectedSkills).slice(0, 10));
        return;
      }

      try {
        const response = await fetch(`/api/v1/skills/search?keyword=${encodeURIComponent(term)}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Failed to search skills (${response.status})`);
        const data: NamedEntityOption[] = await response.json();
        setSkillSuggestions(applySelectedFilter(data, selectedSkills).slice(0, 10));
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.warn('Skill search failed', error);
        }
      }
    };

    const id = window.setTimeout(run, 200);
    return () => {
      window.clearTimeout(id);
      controller.abort();
    };
  }, [skillQuery, skills, selectedSkills]);

  const occupationSuggestions = useMemo(() => {
    const term = occupationQuery.trim().toLowerCase();
    const available = applySelectedFilter(occupations, selectedOccupations);
    const filtered = term.length === 0
      ? available
      : available.filter((occupation) => occupation.name.toLowerCase().includes(term));
    return sortByCount(filtered, 'job_listing_count').slice(0, 10);
  }, [occupationQuery, occupations, selectedOccupations]);

  useEffect(() => {
    const controller = new AbortController();
    const term = industryQuery.trim();

    const run = async () => {
      if (term.length === 0) {
        const suggestions = sortByCount(industries, 'job_listing_total_count');
        setIndustrySuggestions(applySelectedFilter(suggestions, selectedIndustries).slice(0, 10));
        return;
      }

      try {
        const response = await fetch(`/api/v1/industries/search?keyword=${encodeURIComponent(term)}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Failed to search industries (${response.status})`);
        const data: NamedEntityOption[] = await response.json();
        setIndustrySuggestions(applySelectedFilter(data, selectedIndustries).slice(0, 10));
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.warn('Industry search failed', error);
        }
      }
    };

    const id = window.setTimeout(run, 200);
    return () => {
      window.clearTimeout(id);
      controller.abort();
    };
  }, [industryQuery, industries, selectedIndustries]);

  const handleChange = (field: keyof JobListingFormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const addSkill = (skill: NamedEntityOption) => {
    if (selectedSkills.some((item) => item.id === skill.id)) return;
    setSelectedSkills((prev) => [...prev, skill]);
    setSkillQuery('');
  };

  const removeSkill = (skillId: number) => {
    setSelectedSkills((prev) => prev.filter((item) => item.id !== skillId));
  };

  const addOccupation = (occupation: NamedEntityOption) => {
    if (selectedOccupations.some((item) => item.id === occupation.id)) return;
    setSelectedOccupations((prev) => [...prev, occupation]);
    setOccupationQuery('');
  };

  const removeOccupation = (occupationId: number) => {
    setSelectedOccupations((prev) => prev.filter((item) => item.id !== occupationId));
  };

  const addIndustry = (industry: NamedEntityOption) => {
    if (selectedIndustries.some((item) => item.id === industry.id)) return;
    setSelectedIndustries((prev) => [...prev, industry]);
    setIndustryQuery('');
  };

  const removeIndustry = (industryId: number) => {
    setSelectedIndustries((prev) => prev.filter((item) => item.id !== industryId));
  };

  const addRecommendationPoint = () => {
    setForm((prev) => ({
      ...prev,
      recommendation_points: [...prev.recommendation_points, '']
    }));
  };

  const removeRecommendationPoint = (index: number) => {
    setForm((prev) => ({
      ...prev,
      recommendation_points: prev.recommendation_points.filter((_, i) => i !== index)
    }));
  };

  const updateRecommendationPoint = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      recommendation_points: prev.recommendation_points.map((point, i) => 
        i === index ? value : point
      )
    }));
  };

  const renderSelectedPills = (
    items: NamedEntityOption[],
    onRemove: (id: number) => void
  ) => {
    if (items.length === 0) {
      return <p className="text-muted small mb-0">まだ選択されていません。</p>;
    }

    return (
      <div className="d-flex flex-wrap gap-2">
        {items.map((item) => (
          <span key={item.id} className="badge bg-primary-subtle text-primary border fw-normal d-flex align-items-center">
            <span>{item.name}</span>
            <button
              type="button"
              className="btn btn-link btn-sm text-primary ms-2 p-0"
              onClick={() => onRemove(item.id)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    );
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 mb-1">{formHeading}</h1>
          <p className="text-muted mb-0">{formDescription}</p>
        </div>
        <a href={backUrl} className="btn btn-outline-secondary">求人一覧に戻る</a>
      </div>

      <form action={postUrl} method="post" onSubmit={handleSubmit}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        {methodOverride && <input type="hidden" name="_method" value={methodOverride} />}
        {selectedSkills.map((skill) => (
          <input key={`skill-${skill.id}`} type="hidden" name="job_listing[skill_ids][]" value={skill.id} />
        ))}
        {selectedOccupations.map((occupation) => (
          <input key={`occupation-${occupation.id}`} type="hidden" name="job_listing[occupation_ids][]" value={occupation.id} />
        ))}
        {selectedIndustries.map((industry) => (
          <input key={`industry-${industry.id}`} type="hidden" name="job_listing[industry_ids][]" value={industry.id} />
        ))}
        <input type="hidden" name="job_listing[recommendation_points]" value={JSON.stringify({ points: form.recommendation_points.filter(p => p.trim() !== '') })} />

        {errors.length > 0 && (
          <div className="alert alert-danger" role="alert">
            <p className="mb-2 fw-semibold">入力内容を確認してください。</p>
            <ul className="mb-0">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h2 className="h5 mb-0">案件情報</h2>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">案件番号 <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={form.internal_job_listing_id}
                      onChange={handleChange('internal_job_listing_id')}
                      name="job_listing[internal_job_listing_id]"
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        void generateInternalJobListingId();
                      }}
                      disabled={isGeneratingInternalId}
                    >
                      {isGeneratingInternalId ? '生成中…' : 'IDを自動生成'}
                    </button>
                  </div>
                  <div className="form-text">エージェント内で一意になるIDを指定してください。自動生成ボタンから安全なIDを取得することもできます。</div>
                  {internalIdError && <div className="text-danger small mt-1">{internalIdError}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">案件タイトル <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.title}
                    onChange={handleChange('title')}
                    name="job_listing[title]"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">掲載企業名 <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.company_name}
                    onChange={handleChange('company_name')}
                    name="job_listing[company_name]"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">案件URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={form.url}
                    onChange={handleChange('url')}
                    name="job_listing[url]"
                    placeholder="https://example.com/your-job"
                  />
                </div>
              </div>
            </div>

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h2 className="h5 mb-0">スキル・職種・業界</h2>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <label className="form-label fw-semibold">スキル</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="スキル名で検索"
                    value={skillQuery}
                    onChange={(event) => setSkillQuery(event.target.value)}
                  />
                  <div className="mt-3">
                    {skillSuggestions.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {skillSuggestions.map((skill) => (
                          <button
                            type="button"
                            key={skill.id}
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => addSkill(skill)}
                          >
                            {skill.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted small mb-0">条件に合うスキルが見つかりません。</p>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-muted small mb-1">選択済みスキル</p>
                    {renderSelectedPills(selectedSkills, removeSkill)}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">職種</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="職種名で検索"
                    value={occupationQuery}
                    onChange={(event) => setOccupationQuery(event.target.value)}
                  />
                  <div className="mt-3">
                    {occupationSuggestions.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {occupationSuggestions.map((occupation) => (
                          <button
                            type="button"
                            key={occupation.id}
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => addOccupation(occupation)}
                          >
                            {occupation.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted small mb-0">条件に合う職種が見つかりません。</p>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-muted small mb-1">選択済み職種</p>
                    {renderSelectedPills(selectedOccupations, removeOccupation)}
                  </div>
                </div>

                <div>
                  <label className="form-label fw-semibold">業界</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="業界名で検索"
                    value={industryQuery}
                    onChange={(event) => setIndustryQuery(event.target.value)}
                  />
                  <div className="mt-3">
                    {industrySuggestions.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {industrySuggestions.map((industry) => (
                          <button
                            type="button"
                            key={industry.id}
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => addIndustry(industry)}
                          >
                            {industry.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted small mb-0">条件に合う業界が見つかりません。</p>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-muted small mb-1">選択済み業界</p>
                    {renderSelectedPills(selectedIndustries, removeIndustry)}
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h2 className="h5 mb-0">作業内容・要件</h2>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">作業内容</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={form.description}
                    onChange={handleChange('description')}
                    name="job_listing[description]"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">必須要件</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={form.required_skill}
                    onChange={handleChange('required_skill')}
                    name="job_listing[required_skill]"
                  />
                </div>

                <div>
                  <label className="form-label fw-semibold">歓迎要件</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={form.preferred_skill}
                    onChange={handleChange('preferred_skill')}
                    name="job_listing[preferred_skill]"
                  />
                </div>
              </div>
            </div>

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h2 className="h5 mb-0">稼働条件</h2>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">稼働日数（Enum）</label>
                    <select
                      className="form-select"
                      value={form.availability_type}
                      onChange={handleChange('availability_type')}
                      name="job_listing[availability_type]"
                    >
                      <option value="">選択してください</option>
                      {availabilityTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">稼働ボリューム（テキスト）</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.availability}
                      onChange={handleChange('availability')}
                      name="job_listing[availability]"
                      placeholder="例：週3日〜"
                    />
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">勤務形態（Enum）</label>
                    <select
                      className="form-select"
                      value={form.workstyle_type}
                      onChange={handleChange('workstyle_type')}
                      name="job_listing[workstyle_type]"
                    >
                      <option value="">選択してください</option>
                      {workstyleTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">勤務形態詳細（テキスト）</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.workstyle}
                      onChange={handleChange('workstyle')}
                      name="job_listing[workstyle]"
                      placeholder="例：フルリモート可 / 常駐"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h2 className="h5 mb-0">表示情報</h2>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">勤務地（表示用）</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.location_name}
                      onChange={handleChange('location_name')}
                      name="job_listing[location_name]"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">契約形態（表示用）</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.contract_type_name}
                      onChange={handleChange('contract_type_name')}
                      name="job_listing[contract_type_name]"
                    />
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">最低単価</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.min_reward}
                      onChange={handleChange('min_reward')}
                      name="job_listing[min_reward]"
                      min={0}
                      step={1000}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">最高単価</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.max_reward}
                      onChange={handleChange('max_reward')}
                      name="job_listing[max_reward]"
                      min={0}
                      step={1000}
                    />
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">単価単位</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.reward_unit}
                      onChange={handleChange('reward_unit')}
                      name="job_listing[reward_unit]"
                      placeholder="月 / 時 など"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">ステータス</label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={handleChange('status')}
                      name="job_listing[status]"
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h2 className="h5 mb-0">おすすめポイント</h2>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">AI生成のおすすめポイント</label>
                  <div className="mb-3">
                    {form.recommendation_points.map((point, index) => (
                      <div key={index} className="input-group mb-2">
                        <input
                          type="text"
                          className="form-control"
                          value={point}
                          onChange={(e) => updateRecommendationPoint(index, e.target.value)}
                          placeholder="おすすめポイントを入力"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeRecommendationPoint(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={addRecommendationPoint}
                  >
                    + 項目を追加
                  </button>
                  <div className="form-text">
                    フリーランスエンジニアにとって魅力的なポイントを箇条書きで入力してください。
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3">
              <a href={backUrl} className="btn btn-outline-secondary">キャンセル</a>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? submittingText : submitText}
              </button>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h2 className="h6 mb-0">入力のヒント</h2>
              </div>
              <div className="card-body">
                <ul className="list-unstyled small mb-0">
                  <li className="mb-3">
                    <strong>案件番号</strong><br />
                    既に登録済みの案件と重複しないIDを設定してください。
                  </li>
                  <li className="mb-3">
                    <strong>スキル・職種</strong><br />
                    検索ボックスから複数選択できます。追加後に×ボタンで削除できます。
                  </li>
                  <li className="mb-3">
                    <strong>CSVインポート</strong><br />
                    大量の求人を登録する場合は求人一覧ページのCSVインポート機能もご利用いただけます。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default JobListingForm;
